<template>
  <div class="fan-temp-editor space-y-5">
    <!-- Sensor -->
    <section class="fan-temp-editor__section">
      <h4 class="fan-temp-editor__section-title">{{ tt('Sensor', 'Senzor') }}</h4>
      <div class="fan-temp-editor__segmented">
        <button
          type="button"
          :class="{ 'fan-temp-editor__segment--active': config.sensor_type === 'temperature' }"
          @click="update({ sensor_type: 'temperature' })">
          {{ tt('Temperature', 'Temperatura') }}
        </button>
        <button
          type="button"
          :class="{ 'fan-temp-editor__segment--active': config.sensor_type === 'humidity' }"
          @click="update({ sensor_type: 'humidity' })">
          {{ tt('Humidity', 'Vlaga') }}
        </button>
        <button
          type="button"
          :class="{ 'fan-temp-editor__segment--active': config.sensor_type === 'temperature_humidity' }"
          @click="update({ sensor_type: 'temperature_humidity' })">
          {{ tt('Temperature + Humidity', 'Temperatura + vlaga') }}
        </button>
      </div>
      <p class="fan-temp-editor__hint">
        {{
          tt(
            'With both sensors bound and “Temperature + Humidity” selected, the sensor that demands the higher speed wins.',
            'Če sta povezana oba senzorja in je izbrano »Temperatura + vlaga«, zmaga senzor, ki zahteva višjo hitrost.',
          )
        }}
      </p>
    </section>

    <!-- Regulation -->
    <section class="fan-temp-editor__section">
      <h4 class="fan-temp-editor__section-title">{{ tt('Regulation', 'Uravnavanje') }}</h4>
      <div class="fan-temp-editor__segmented">
        <button
          type="button"
          :class="{ 'fan-temp-editor__segment--active': config.control_mode === 'threshold' }"
          @click="update({ control_mode: 'threshold' })">
          {{ tt('Threshold bands', 'Mejna območja') }}
        </button>
        <button
          type="button"
          :class="{ 'fan-temp-editor__segment--active': config.control_mode === 'pid' }"
          @click="update({ control_mode: 'pid' })">
          PID
        </button>
      </div>
    </section>

    <!-- Threshold bands -->
    <section v-if="config.control_mode === 'threshold'" class="fan-temp-editor__section">
      <h4 class="fan-temp-editor__section-title">{{ tt('Threshold bands', 'Mejna območja') }}</h4>

      <div class="fan-temp-editor__grid">
        <label class="fan-temp-editor__number">
          <span>{{ tt('Low threshold', 'Spodnja meja') }}</span>
          <input
            type="number"
            step="0.5"
            :value="config.low_threshold"
            @change="update({ low_threshold: Number(($event.target as HTMLInputElement).value) || 0 })" />
          <span>{{ config.sensor_type === 'humidity' ? '%' : '°' }}</span>
        </label>
        <label class="fan-temp-editor__number">
          <span>{{ tt('High threshold', 'Zgornja meja') }}</span>
          <input
            type="number"
            step="0.5"
            :value="config.high_threshold"
            @change="update({ high_threshold: Number(($event.target as HTMLInputElement).value) || 0 })" />
          <span>{{ config.sensor_type === 'humidity' ? '%' : '°' }}</span>
        </label>
        <label class="fan-temp-editor__number">
          <span>{{ tt('Hysteresis', 'Histereza') }}</span>
          <input
            type="number"
            min="0"
            step="0.5"
            :value="config.hysteresis"
            @change="update({ hysteresis: Math.max(0, Number(($event.target as HTMLInputElement).value) || 0) })" />
          <span></span>
        </label>
      </div>

      <div class="fan-temp-editor__speed-slider">
        <div class="fan-temp-editor__slider-head">
          <span class="fan-temp-editor__label">{{
            tt('Below low threshold — idle speed', 'Pod spodnjo mejo — hitrost mirovanja')
          }}</span>
          <span class="fan-temp-editor__slider-value">{{ config.idle_speed }}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          :value="config.idle_speed"
          @input="update({ idle_speed: Number(($event.target as HTMLInputElement).value) })" />
      </div>
      <div class="fan-temp-editor__speed-slider">
        <div class="fan-temp-editor__slider-head">
          <span class="fan-temp-editor__label">{{
            tt('Between thresholds — low speed', 'Med mejama — nizka hitrost')
          }}</span>
          <span class="fan-temp-editor__slider-value">{{ config.low_speed }}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          :value="config.low_speed"
          @input="update({ low_speed: Number(($event.target as HTMLInputElement).value) })" />
      </div>
      <div class="fan-temp-editor__speed-slider">
        <div class="fan-temp-editor__slider-head">
          <span class="fan-temp-editor__label">{{
            tt('Above high threshold — max speed', 'Nad zgornjo mejo — največja hitrost')
          }}</span>
          <span class="fan-temp-editor__slider-value">{{ config.max_speed }}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          :value="config.max_speed"
          @input="update({ max_speed: Number(($event.target as HTMLInputElement).value) })" />
      </div>
    </section>

    <!-- PID -->
    <section v-if="config.control_mode === 'pid'" class="fan-temp-editor__section">
      <h4 class="fan-temp-editor__section-title">{{ tt('PID loop', 'Zanka PID') }}</h4>

      <label class="fan-temp-editor__check">
        <input
          type="checkbox"
          :checked="config.pid.enabled"
          @change="updatePid({ enabled: ($event.target as HTMLInputElement).checked })" />
        <span>{{ tt('Enable PID regulation', 'Omogoči uravnavanje PID') }}</span>
      </label>

      <div class="fan-temp-editor__grid">
        <label class="fan-temp-editor__number">
          <span>{{ tt('Setpoint', 'Nastavljena vrednost') }}</span>
          <input
            type="number"
            step="0.5"
            :value="config.pid.setpoint"
            @change="updatePid({ setpoint: Number(($event.target as HTMLInputElement).value) || 0 })" />
          <span>{{ config.sensor_type === 'humidity' ? '%' : '°' }}</span>
        </label>
        <label class="fan-temp-editor__number">
          <span>Kp</span>
          <input
            type="number"
            step="0.1"
            :value="config.pid.kp"
            @change="updatePid({ kp: Number(($event.target as HTMLInputElement).value) || 0 })" />
          <span></span>
        </label>
        <label class="fan-temp-editor__number">
          <span>Ki</span>
          <input
            type="number"
            step="0.1"
            :value="config.pid.ki"
            @change="updatePid({ ki: Number(($event.target as HTMLInputElement).value) || 0 })" />
          <span></span>
        </label>
        <label class="fan-temp-editor__number">
          <span>Kd</span>
          <input
            type="number"
            step="0.1"
            :value="config.pid.kd"
            @change="updatePid({ kd: Number(($event.target as HTMLInputElement).value) || 0 })" />
          <span></span>
        </label>
        <label class="fan-temp-editor__number">
          <span>{{ tt('Sample interval', 'Interval vzorčenja') }}</span>
          <input
            type="number"
            min="1"
            step="1"
            :value="config.pid.sample_interval_seconds"
            @change="
              updatePid({
                sample_interval_seconds: Math.max(1, Number(($event.target as HTMLInputElement).value) || 30),
              })
            " />
          <span>{{ tt('sec', 'sek') }}</span>
        </label>
        <label class="fan-temp-editor__number">
          <span>{{ tt('Min output', 'Najmanjši izhod') }}</span>
          <input
            type="number"
            min="0"
            max="100"
            :value="config.pid.min_output"
            @change="updatePid({ min_output: clampNum(Number(($event.target as HTMLInputElement).value), 0, 100) })" />
          <span>%</span>
        </label>
        <label class="fan-temp-editor__number">
          <span>{{ tt('Max output', 'Največji izhod') }}</span>
          <input
            type="number"
            min="0"
            max="100"
            :value="config.pid.max_output"
            @change="updatePid({ max_output: clampNum(Number(($event.target as HTMLInputElement).value), 0, 100) })" />
          <span>%</span>
        </label>
      </div>
      <p class="fan-temp-editor__hint">
        {{
          tt(
            'PID uses the temperature sensor (or humidity when no temperature is bound). Output is clamped between min and max.',
            'PID uporablja senzor temperature (ali vlago, kadar temperatura ni povezana). Izhod je omejen med najmanjšo in največjo vrednostjo.',
          )
        }}
      </p>
    </section>

    <!-- Delays -->
    <section class="fan-temp-editor__section">
      <h4 class="fan-temp-editor__section-title">{{ tt('Delays', 'Zakasnitve') }}</h4>
      <div class="fan-temp-editor__grid">
        <label class="fan-temp-editor__number">
          <span>{{ tt('Speed-up delay', 'Zakasnitev pospešitve') }}</span>
          <input
            type="number"
            min="0"
            step="5"
            :value="config.delay_on_seconds"
            @change="
              update({ delay_on_seconds: Math.max(0, Number(($event.target as HTMLInputElement).value) || 0) })
            " />
          <span>{{ tt('sec', 'sek') }}</span>
        </label>
        <label class="fan-temp-editor__number">
          <span>{{ tt('Slow-down delay', 'Zakasnitev upočasnitve') }}</span>
          <input
            type="number"
            min="0"
            step="10"
            :value="config.delay_off_seconds"
            @change="
              update({ delay_off_seconds: Math.max(0, Number(($event.target as HTMLInputElement).value) || 0) })
            " />
          <span>{{ tt('sec', 'sek') }}</span>
        </label>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  FanPidConfig,
  FanTemperatureControllerConfig,
  normalizeFanTempControllerConfig,
} from '../../../utils/fanAutomation';
import { tt } from '../../../utils/consoleText';

const props = defineProps<{
  modelValue: Record<string, unknown>;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: Record<string, unknown>): void;
}>();

const config = computed<FanTemperatureControllerConfig>(() => normalizeFanTempControllerConfig(props.modelValue));

function commit(next: FanTemperatureControllerConfig) {
  emit('update:modelValue', JSON.parse(JSON.stringify(next)) as Record<string, unknown>);
}

function update(patch: Partial<FanTemperatureControllerConfig>) {
  commit({ ...config.value, ...patch });
}

function updatePid(patch: Partial<FanPidConfig>) {
  commit({ ...config.value, pid: { ...config.value.pid, ...patch } });
}

function clampNum(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}
</script>

<style scoped>
.fan-temp-editor {
  width: 100%;
}

.fan-temp-editor__section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgb(100, 116, 139);
  margin-bottom: 8px;
}

.fan-temp-editor__hint {
  font-size: 11px;
  line-height: 1.45;
  color: rgb(148, 163, 184);
  margin-top: 6px;
}

.fan-temp-editor__segmented {
  display: inline-flex;
  gap: 4px;
  background: rgba(148, 163, 184, 0.12);
  border-radius: 999px;
  padding: 3px;
}

.fan-temp-editor__segmented button {
  font-size: 12px;
  font-weight: 600;
  padding: 5px 14px;
  border-radius: 999px;
  color: rgb(148, 163, 184);
}

.fan-temp-editor__segment--active {
  background: rgb(56, 189, 248);
  color: #fff !important;
}

.fan-temp-editor__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.fan-temp-editor__number {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: rgb(148, 163, 184);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 8px;
  padding: 6px 8px;
}

.fan-temp-editor__number input {
  flex: 1;
  min-width: 0;
  background: transparent;
  color: inherit;
  font-size: 12px;
  text-align: right;
}

.fan-temp-editor__speed-slider {
  margin-top: 10px;
}

.fan-temp-editor__slider-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.fan-temp-editor__label {
  font-size: 11px;
  color: rgb(148, 163, 184);
}

.fan-temp-editor__slider-value {
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.fan-temp-editor__speed-slider input[type='range'] {
  width: 100%;
  accent-color: rgb(56, 189, 248);
}

.fan-temp-editor__check {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgb(107, 114, 128);
  cursor: pointer;
}

.dark .fan-temp-editor__check {
  color: rgb(209, 213, 219);
}

.fan-temp-editor__check input {
  accent-color: rgb(56, 189, 248);
}
</style>
